/* eslint-disable cf-web/codegen-query-returns */
import { useGetProductHistory } from 'cf-menu/hooks/use-store-product-history'
import { ContentAssetType, ContentLanguage } from 'codegen/types'
import * as React from 'react'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { decodeHtmlEntities } from 'utils/decode-html-entities'
import { useAuthContext } from '@repo/cf-user/contexts/auth'
import type { UseQueryResult } from '@tanstack/react-query'
import { useQueries } from '@tanstack/react-query'
import { useLocale } from 'next-intl'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { rootPath } from '@repo/web-utils/root-path'

import type { ContentProductRecentlyViewedQuery } from '@web/gen/content'
import {
  contentProductRecentlyViewedQueryFetcher,
  contentProductRecentlyViewedQueryKey,
} from '@web/gen/content'
import type { LegacyProductRecentlyViewedQuery } from '@web/gen/cf'
import {
  legacyProductRecentlyViewedQueryFetcher,
  legacyProductRecentlyViewedQueryKey,
} from '@web/gen/cf'
import type { ProductCardProduct } from './product-card-product-type'
import { mapCategoryPageTypeToCategoryDetails } from '@web/components/content-api/config'

interface UseRecentlyViewedProducts {
  products: ProductCardProduct[]
  isLoading: boolean
}

export const useRecentlyViewedProductsData = (): UseRecentlyViewedProducts => {
  const { userData } = useAuthContext()

  const productHistory: string[] = useGetProductHistory(userData?.id ?? '')
  const locale = useLocale()

  const numericIds = React.useMemo(
    () => productHistory.filter((id: string) => !isNaN(Number(id))),
    [productHistory],
  )

  const stringIds = React.useMemo(
    () => productHistory.filter((id: string) => isNaN(Number(id))),
    [productHistory],
  )

  const legacyQueries = useQueries({
    queries: numericIds.map((id: string) => {
      const variables = { productId: parseInt(id, 10) }
      return {
        queryKey: legacyProductRecentlyViewedQueryKey(variables),
        queryFn: () => legacyProductRecentlyViewedQueryFetcher(variables)(),
        enabled: true,
      }
    }),
  }) as UseQueryResult<LegacyProductRecentlyViewedQuery, Error>[]

  const contentQueries = useQueries({
    queries: stringIds.map((id: string) => {
      const variables = { id, language: ContentLanguage.LanguageEn }
      return {
        queryKey: contentProductRecentlyViewedQueryKey(variables),
        queryFn: () => contentProductRecentlyViewedQueryFetcher(variables)(),
        enabled: true,
      }
    }),
  }) as UseQueryResult<ContentProductRecentlyViewedQuery, Error>[]

  // Track loading state for all queries
  const isLegacyLoading = legacyQueries.some(query => query.isPending)
  const isContentLoading = contentQueries.some(query => query.isPending)
  const isLoading = isLegacyLoading || isContentLoading

  // Create a map to store products by ID for quick lookup
  const productsMap = new Map()

  // Add legacy products to map
  legacyQueries.forEach(query => {
    if (query.data?.legacyProductsFetchProduct) {
      const legacyProduct = query.data.legacyProductsFetchProduct
      const legacyId = legacyProduct.id.toString()

      // Map legacy product to the expected type
      productsMap.set(legacyId, {
        id: legacyId,
        name: decodeHtmlEntities(legacyProduct.title),
        image: legacyProduct.image,
        author: legacyProduct.author?.name || '',
        price: legacyProduct.regularPrice || 0,
        salePrice: legacyProduct.salePrice || 0,
        favoriteCount: legacyProduct.favoriteCount || 0,
        href: addOrReplaceLocaleInUrl(`${AppRoutes.PRODUCT}${legacyProduct.slug}/`, locale),
        isLegacy: true,
        category: {
          id: legacyProduct.productMainCategory?.id || '',
          title: legacyProduct.productMainCategory?.title || '',
          slug: legacyProduct.productMainCategory?.slug || '',
        },
      })
    }
  })

  // Add content products to map
  contentQueries.forEach(query => {
    if (query.data?.contentProduct) {
      const contentProduct = query.data.contentProduct
      const contentId = contentProduct.id.toString()

      // Get the preview image for other products than assets
      let image =
        contentProduct.assets?.find(
          (asset: { type: ContentAssetType; file_path?: string }) =>
            asset.type === ContentAssetType.AssetTypePreview && asset.file_path,
        )?.file_path || ''

      // Check if it's a stock photo product
      const isStockPhoto =
        contentProduct.category?.root?.id ===
        mapCategoryPageTypeToCategoryDetails.stockphotos.parentId

      if (isStockPhoto) {
        // For stock photos, get the thumbnail with the largest resolution (w200px)
        const thumbnails =
          contentProduct.assets?.filter(
            (asset: {
              type: ContentAssetType
              file_path?: string
              resolution?: { __typename?: string; width: number; height: number } | null
              downloadable?: boolean
            }) => asset.type === ContentAssetType.AssetTypeThumbnail && asset.file_path,
          ) || []

        // Sort thumbnails by resolution (width) in descending order
        const sortedThumbnails = [...thumbnails].toSorted(
          (a, b) => (b.resolution?.width || 0) - (a.resolution?.width || 0),
        )

        // Use the largest thumbnail or fall back to the current image
        image = sortedThumbnails[0]?.file_path || image
      }
      // Get image from assets if available

      // Construct the full image URL with the content images root URL
      const fullImageUrl = image
        ? `${process.env.NEXT_PUBLIC_CONTENT_IMAGES_ROOT_URL}/${image}`
        : ''

      // Map content product to the expected type
      productsMap.set(contentId, {
        id: contentId,
        name: contentProduct.title,
        image: fullImageUrl,
        author: '',
        price: 0,
        salePrice: 0,
        favoriteCount: 0,
        href: addOrReplaceLocaleInUrl(
          `${rootPath}${AppRoutes.PROD}${contentProduct.slug}-${contentProduct.id}/`,
          locale,
        ),
        isLegacy: false,
        category: {
          id: contentProduct.category.root?.id || '',
          title: contentProduct.category.root?.name || '',
          slug: contentProduct.category.root?.slug || '',
        },
      })
    }
  })

  // Map product history to actual products, preserving the original order
  const products = productHistory
    .filter((id: string) => productsMap.has(id))
    .map((id: string) => productsMap.get(id))

  return {
    products,
    isLoading,
  }
}
